import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/useToast';
import i18n from '@/i18n';

/**
 * Expected, user-facing refusals — toasted by the hook, kept out of Sentry by
 * the app's MutationCache (it skips `envelope.code`s listed in
 * `meta.expectedErrorCodes`, see App.tsx).
 */
const DISCIPLINE_EXISTS = 'discipline_exists';
const DISCIPLINE_IN_USE = 'discipline_in_use';
const DISCIPLINE_MUTATION_META = { expectedErrorCodes: [DISCIPLINE_EXISTS, DISCIPLINE_IN_USE] as readonly string[] };
const codedEnvelope = (error: unknown) => (error as { envelope?: { code?: string } } | null)?.envelope?.code;
import { Discipline, CreateDisciplineData } from '@/types/people';

// Query keys
export const disciplineKeys = {
  all: ['disciplines'] as const,
  lists: () => [...disciplineKeys.all, 'list'] as const,
  list: (organizationId: string) => [...disciplineKeys.lists(), organizationId] as const,
};

// Fetch disciplines for an organization
export const useDisciplines = (organizationId?: string) => {
  return useQuery({
    queryKey: disciplineKeys.list(organizationId || ''),
    queryFn: async (): Promise<Discipline[]> => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('disciplines')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching disciplines:', error);
        throw error;
      }

      return data || [];
    },
    enabled: !!organizationId,
  });
};

// Create discipline mutation
export const useCreateDiscipline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: DISCIPLINE_MUTATION_META,
    mutationFn: async (data: CreateDisciplineData & { organization_id: string }) => {
      const { data: discipline, error } = await supabase
        .from('disciplines')
        .insert(data)
        .select()
        .single();

      if (error) {
        // Keep the Postgres code (CreateSkill/DisciplineDialog read 23505 to pick
        // the race winner) and add the expected-refusal envelope.
        if (error.code === '23505' && (error.message.includes('unique_discipline_per_org') || error.message.includes('unique_role_per_org'))) {
          throw Object.assign(error, { envelope: { code: DISCIPLINE_EXISTS } });
        }
        throw error;
      }
      return discipline;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: disciplineKeys.all });
      toast.success(i18n.t('common:people.toast.discipline.created', { name: data.name }));
    },
    onError: (error: Error & { code?: string }) => {
      // Handle unique constraint violations
      if (codedEnvelope(error) === DISCIPLINE_EXISTS) {
        toast.error(i18n.t('common:people.toast.discipline.exists'));
      } else {
        toast.error(i18n.t('common:people.toast.discipline.createFailed'), error.message ? { description: error.message } : undefined);
      }
    },
  });
};

// Update discipline mutation
export const useUpdateDiscipline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<CreateDisciplineData> & { id: string }) => {
      const { id, ...updateData } = data;

      const { data: discipline, error } = await supabase
        .from('disciplines')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return discipline;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: disciplineKeys.all });
      // person_index carries the names (people grid liveness).
      queryClient.invalidateQueries({ queryKey: ['people'] });
      toast.success(i18n.t('common:people.toast.discipline.updated', { name: data.name }));
    },
    onError: (error: Error) => {
      toast.error(i18n.t('common:people.toast.discipline.updateFailed'), error.message ? { description: error.message } : undefined);
    },
  });
};

// Delete discipline mutation
export const useDeleteDiscipline = () => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: DISCIPLINE_MUTATION_META,
    mutationFn: async (disciplineId: string) => {
      // First check if discipline is in use
      const { data: usageCheck } = await supabase
        .from('person_disciplines')
        .select('id')
        .eq('discipline_id', disciplineId)
        .limit(1);

      if (usageCheck && usageCheck.length > 0) {
        throw Object.assign(new Error(i18n.t('common:people.toast.discipline.inUse')), {
          envelope: { code: DISCIPLINE_IN_USE },
        });
      }

      const { error } = await supabase
        .from('disciplines')
        .delete()
        .eq('id', disciplineId);

      if (error) throw error;
      return disciplineId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: disciplineKeys.all });
      // person_index carries the names (people grid liveness).
      queryClient.invalidateQueries({ queryKey: ['people'] });
      toast.success(i18n.t('common:people.toast.discipline.deleted'));
    },
    onError: (error: Error) => {
      toast.error(i18n.t('common:people.toast.discipline.deleteFailed'), error.message ? { description: error.message } : undefined);
    },
  });
};

// Get discipline usage statistics
export const useDisciplineUsage = (disciplineId?: string) => {
  return useQuery({
    queryKey: ['disciplines', 'usage', disciplineId],
    queryFn: async () => {
      if (!disciplineId) return { count: 0, people: [] };

      const { data, error } = await supabase
        .from('person_disciplines')
        .select(`
          person:people(id, first_name, last_name, code)
        `)
        .eq('discipline_id', disciplineId);

      if (error) {
        console.error('Error fetching discipline usage:', error);
        throw error;
      }

      return {
        count: data?.length || 0,
        people: data?.map(item => item.person).filter(Boolean) || [],
      };
    },
    enabled: !!disciplineId,
  });
};